import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import {
  getProductBySlug,
  getCategoryDescription,
} from '../ProductCategory/products';
import { getCategoryBySlug } from '../Categories/constant';
import {
  PageContainer,
  Container,
  Breadcrumbs,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbCurrent,
  ProductContent,
  ImageSection,
  InfoSection,
  ProductImage,
  ProductTitle,
  ProductCategory,
  ProductDescription,
  DetailGrid,
  DetailItem,
  DetailLabel,
  DetailValue,
  EnquiryButton,
  ImageContainer,
  ThumbnailContainer,
  ThumbnailBox,
  ThumbnailImage,
} from './ProductDetail.styled';

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isAnimationPaused, setIsAnimationPaused] = useState(false);

  const product = getProductBySlug(slug);
  const categorySlug = product?.categorySlug;
  const category = categorySlug ? getCategoryBySlug(categorySlug) : null;

  const images = React.useMemo(() => {
    return product?.appImage
      ? [product.image, product.appImage]
      : [product?.image];
  }, [product]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setCurrentImageIndex(0);
    setIsAnimationPaused(false);
  }, [slug]);

  useEffect(() => {
    if (images.length > 1 && !isAnimationPaused) {
      const interval = setInterval(() => {
        if (!isHovered) {
          setCurrentImageIndex((prev) => (prev === 0 ? 1 : 0));
        }
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [images.length, isHovered, isAnimationPaused]);

  const handleThumbnailClick = (index) => {
    setCurrentImageIndex(index);
    setIsAnimationPaused(true);
  };

  if (!product || !category) {
    return (
      <PageContainer>
        <Header />
        <Container
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          <ProductCategory>Error 404</ProductCategory>
          <ProductTitle>Product Not Found</ProductTitle>
          <ProductDescription>
            We couldn't find the product you're looking for. It might have been
            moved or deleted.
          </ProductDescription>
          <EnquiryButton onClick={() => navigate('/products')}>
            Back to Products
          </EnquiryButton>
        </Container>
        <Footer />
      </PageContainer>
    );
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  return (
    <PageContainer>
      <Header />
      <Container
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <Breadcrumbs variants={itemVariants}>
          <BreadcrumbLink to="/products">PRODUCTS</BreadcrumbLink>
          <BreadcrumbSeparator>|</BreadcrumbSeparator>
          <BreadcrumbLink to={`/products/${categorySlug}`}>
            {category.name}
          </BreadcrumbLink>
          <BreadcrumbSeparator>|</BreadcrumbSeparator>
          <BreadcrumbCurrent>{product.name}</BreadcrumbCurrent>
        </Breadcrumbs>

        <ProductContent>
          <div>
            <ImageSection
              variants={itemVariants}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <ImageContainer>
                <AnimatePresence mode="wait">
                  <ProductImage
                    key={images[currentImageIndex]}
                    src={images[currentImageIndex]}
                    alt={product.name}
                    $isAppImage={currentImageIndex === 1}
                    initial={{ opacity: 0, x: 20, scale: 0.95 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{ opacity: 0, x: -20, scale: 1.05 }}
                    transition={{
                      duration: 0.8,
                      ease: [0.25, 0.1, 0.25, 1],
                    }}
                    onError={(e) => {
                      e.target.src =
                        'https://via.placeholder.com/600x600?text=' +
                        encodeURIComponent(product.name);
                    }}
                  />
                </AnimatePresence>
              </ImageContainer>
            </ImageSection>
            <ThumbnailContainer>
              <ThumbnailBox
                $isActive={currentImageIndex === 0}
                onClick={() => handleThumbnailClick(0)}
              >
                <ThumbnailImage
                  src={images[0]}
                  alt={`${product.name} thumbnail 1`}
                  onError={(e) => {
                    e.target.src =
                      'https://via.placeholder.com/100x100?text=' +
                      encodeURIComponent(product.name);
                  }}
                />
              </ThumbnailBox>
              <ThumbnailBox
                $isActive={currentImageIndex === 1}
                onClick={() => handleThumbnailClick(1)}
              >
                <ThumbnailImage
                  src={images[1] || images[0]}
                  alt={`${product.name} thumbnail 2`}
                  onError={(e) => {
                    e.target.src =
                      'https://via.placeholder.com/100x100?text=' +
                      encodeURIComponent(product.name);
                  }}
                />
              </ThumbnailBox>
            </ThumbnailContainer>
          </div>

          <InfoSection variants={itemVariants}>
            <ProductCategory>{category.name}</ProductCategory>
            <ProductTitle>{product.name}</ProductTitle>
            <ProductDescription>
              {product.description || getCategoryDescription(categorySlug)}
            </ProductDescription>

            <DetailGrid>
              {product.material && (
                <DetailItem>
                  <DetailLabel>Material</DetailLabel>
                  <DetailValue>{product.material}</DetailValue>
                </DetailItem>
              )}
              {product.finish && (
                <DetailItem>
                  <DetailLabel>Finish</DetailLabel>
                  <DetailValue>{product.finish}</DetailValue>
                </DetailItem>
              )}
              {product.size && (
                <DetailItem>
                  <DetailLabel>Size</DetailLabel>
                  <DetailValue>{product.size}</DetailValue>
                </DetailItem>
              )}
              {product.thickness && (
                <DetailItem>
                  <DetailLabel>Thickness</DetailLabel>
                  <DetailValue>{product.thickness}</DetailValue>
                </DetailItem>
              )}
              {product.height && (
                <DetailItem>
                  <DetailLabel>Height</DetailLabel>
                  <DetailValue>{product.height}</DetailValue>
                </DetailItem>
              )}
              {product.grade && (
                <DetailItem>
                  <DetailLabel>Grade</DetailLabel>
                  <DetailValue>{product.grade}</DetailValue>
                </DetailItem>
              )}
              {product.length && (
                <DetailItem>
                  <DetailLabel>Length</DetailLabel>
                  <DetailValue>{product.length}</DetailValue>
                </DetailItem>
              )}
              {product.quantity && (
                <DetailItem>
                  <DetailLabel>Quantity</DetailLabel>
                  <DetailValue>{product.quantity}</DetailValue>
                </DetailItem>
              )}
              {product.shape && (
                <DetailItem>
                  <DetailLabel>Shape</DetailLabel>
                  <DetailValue>{product.shape}</DetailValue>
                </DetailItem>
              )}
            </DetailGrid>

            <EnquiryButton
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/contact')}
            >
              Enquire About This Product
            </EnquiryButton>
          </InfoSection>
        </ProductContent>
      </Container>
      <Footer />
    </PageContainer>
  );
};

export default ProductDetail;
